import { onlineManager, type QueryClient } from '@tanstack/react-query'
import { redirect } from 'react-router-dom'
import { queryClient } from '@/lib/api-client.ts'
import { userQueryKey } from '@/lib/actions.ts'
import { useUserStore, type User } from '@/store/user.ts'
import { readLastUsedIssueType } from '@/routes/projectStages/issues/lib/lastUsedIssueType.ts'
import { cachedStageIssueTypes, issueTypeIri } from '@/lib/quickCapture/quickCaptureIssueTypes.ts'
import { currStageIssueTypesQuery } from '@/lib/actions/getStageIssuesTypes.ts'
import { isQuickCapturePhone } from '@/lib/quickCapture/isQuickCapturePhone.ts'
import { requireQuickCapture } from '@/lib/quickCapture/quickCaptureAccess.ts'

type LoaderArgsT = {
  params: {
    projectStageId?: string
  }
}

/** The user id from cache only — this path must not wait on the network for a redirect. */
function cachedUserId(client: QueryClient): number | undefined {
  return client.getQueryData<User>(userQueryKey)?.id ?? useUserStore.getState().user?.id
}

/**
 * Where a device that is not a phone lands instead of the capture screen: the stage's issue list
 * on the type the user last created with (if the stage still has it), else on the stage's first
 * type, else the stage dashboard. Read from cache only; the loader warms the cache first (see
 * `warmStageIssueTypes`), the screen's own mount-time check runs after the stage loader did.
 */
export function quickCaptureListPath(client: QueryClient, projectStageId: string): string {
  const issueTypes = cachedStageIssueTypes(client, projectStageId)
  const lastUsedIri = readLastUsedIssueType(cachedUserId(client), projectStageId)
  const issueType =
    issueTypes.find(candidate => issueTypeIri(candidate) === lastUsedIri) ?? issueTypes[0]
  if (!issueType) return `/project_stages/${projectStageId}/dashboard`
  return `/project_stages/${projectStageId}/issue_types/${issueType.id}/issues`
}

/**
 * A direct URL opened cold on a desktop runs this loader in parallel with the stage loader that
 * caches the issue types, so the cache is usually empty here and the redirect would land on the
 * dashboard instead of the list. Fetch the types once when online. Offline the fetch is skipped
 * rather than awaited: React Query pauses an offline query, and a paused fetch never settles.
 * Any failure falls through to the dashboard.
 */
async function warmStageIssueTypes(client: QueryClient, projectStageId: string): Promise<void> {
  if (!projectStageId || cachedStageIssueTypes(client, projectStageId).length > 0) return
  if (!onlineManager.isOnline()) return
  try {
    await client.fetchQuery(currStageIssueTypesQuery(projectStageId))
  } catch {
    // The dashboard is still a valid place to land.
  }
}

type QuickCaptureLoaderDepsT = {
  isPhone?: () => boolean
  guard?: (args: LoaderArgsT) => Promise<Response | null>
  /** A getter, so the shared client is read when the loader runs, not when it is created. */
  client?: () => QueryClient
}

/**
 * Route loader for `/project_stages/:projectStageId/quick-capture`.
 *
 * Not a phone: straight to the issue list, without consulting the pilot flag — a desktop is
 * never let in, whatever the flag says, so asking would only cost a request. A phone: whatever
 * `requireQuickCapture` decides (null lets it in, else the dashboard). Pair with
 * `shouldRevalidateQuickCapture`, as the guard's docblock requires.
 */
export function createQuickCaptureLoader(deps: QuickCaptureLoaderDepsT = {}) {
  return async (args: LoaderArgsT): Promise<Response | null> => {
    const isPhone = deps.isPhone ?? (() => isQuickCapturePhone())
    if (!isPhone()) {
      const client = deps.client?.() ?? queryClient
      const projectStageId = args.params.projectStageId ?? ''
      await warmStageIssueTypes(client, projectStageId)
      return redirect(quickCaptureListPath(client, projectStageId))
    }
    return (deps.guard ?? requireQuickCapture)(args)
  }
}

export const quickCaptureLoader = createQuickCaptureLoader()
